import { Card, Dropdown, Tag } from "@digdir/designsystemet-react";
import {
  Buildings3Icon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@navikt/aksel-icons";
import { useRouter } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { AvtaleDto, AvtaleStatus } from "@/api/generated";
import { DescriptionList, DescriptionTerm } from "@/components";
import {
  dateHasPassed,
  formatDateString,
  formatDateStringInterval,
  formatOrgNr,
  formatPostnummer,
} from "@/utils";
import { UgyldigAvtaleSection } from "./UgyldigAvtaleSection";

type AvtaleProps = {
  avtale: AvtaleDto;
};

const avtaleStatusDataColor = (status: AvtaleStatus): string => {
  switch (status) {
    case "Aktiv":
      return "success";
    case "Avbestilt":
      return "warning";
    case "Utgått":
      return "accent";
    case "Ugyldig":
      return "danger";
    default:
      return "";
  }
};

export const Avtale = ({ avtale }: AvtaleProps) => {
  const { t } = useTranslation();
  const router = useRouter();
  const [open, setOpen] = useState(false);

  const avtaleBaseUrl = `/avtaler/${avtale.avtaleId}/${avtale.avtaleType === "Privat" ? "privat" : "selskap"}`;

  return (
    <div className="flex flex-col gap-1" data-testid="avtale-card">
      <Card>
        <Tag data-color={avtaleStatusDataColor(avtale.avtaleStatus)}>
          {t("pages.avtaler.statuser.avtaleStatus", {
            context: avtale.avtaleStatus,
          })}
        </Tag>
        <div className="flex flex-col mb-4 sm:flex-row sm:justify-between sm:items-center">
          <div className="flex flex-col gap-1">
            <div className="text-sm text-balder-text-default">
              {avtale.avtaleStatus === "Avbestilt" &&
              avtale.cancelledAt != null ? (
                <span>
                  {t("pages.avtaler.avtaler.detaljer.avbestilt")}:{" "}
                  {formatDateString(avtale.cancelledAt)}
                </span>
              ) : (
                <span>
                  {t("pages.avtaler.avtaler.detaljer.avtaleperiode")}:{" "}
                  {formatDateStringInterval(
                    avtale.activeFromInclusive,
                    avtale.activeToExclusive,
                  )}
                </span>
              )}
            </div>
            <div className="flex gap-2 items-center">
              <Buildings3Icon fontSize={`${24}px`} />
              <span className="text-2xl font-semibold text-balder-text-default">
                {avtale.leverandør?.name ??
                  formatOrgNr(avtale.leverandør?.organisasjonsnummer)}
              </span>
            </div>
          </div>
          {avtale.avtaleStatus === "Aktiv" && (
            <Dropdown.TriggerContext>
              <Dropdown.Trigger
                variant="secondary"
                className="mt-4 sm:mt-0"
                onClick={() => setOpen((prev) => !prev)}
              >
                <div className="flex gap-2 items-center">
                  <span>{t("pages.avtaler.avtaler.valg.tittel")}</span>
                  {open ? (
                    <ChevronUpIcon fontSize={`1rem`} aria-hidden />
                  ) : (
                    <ChevronDownIcon fontSize={`1rem`} aria-hidden />
                  )}
                </div>
              </Dropdown.Trigger>
              <Dropdown
                placement="bottom-end"
                open={open}
                onClose={() => setOpen(false)}
              >
                <Dropdown.List>
                  <Dropdown.Item>
                    {!dateHasPassed(avtale.avbestillingsFrist) && (
                      <Dropdown.Button
                        onClick={() =>
                          router.navigate({
                            to: `${avtaleBaseUrl}/endre`,
                          })
                        }
                      >
                        {t("pages.avtaler.avtaler.valg.endre")}
                      </Dropdown.Button>
                    )}
                    <Dropdown.Button
                      onClick={() =>
                        router.navigate({
                          to: `${avtaleBaseUrl}/bekreftelse`,
                        })
                      }
                    >
                      {t("pages.avtaler.avtaler.valg.vis-kvittering")}
                    </Dropdown.Button>
                    {!dateHasPassed(avtale.avbestillingsFrist) && (
                      <Dropdown.Button
                        onClick={() =>
                          router.navigate({
                            to: `${avtaleBaseUrl}/avbestill`,
                          })
                        }
                      >
                        {t("pages.avtaler.avtaler.valg.avbestill")}
                      </Dropdown.Button>
                    )}
                  </Dropdown.Item>
                </Dropdown.List>
              </Dropdown>
            </Dropdown.TriggerContext>
          )}
        </div>
        <DescriptionList className="text-sm text-balder-text-default">
          <DescriptionTerm
            tranlsationFeltKey="pages.avtaler.avtaler.detaljer.typeBolig"
            value={t("enums.boligType", {
              context:
                avtale.avtaleType === "Selskap"
                  ? "Boliglag"
                  : avtale.avtaleDetaljerPrivat?.boligType,
            })}
          />
          {avtale.avtaleType === "Privat" ? (
            <DescriptionTerm
              tranlsationFeltKey="pages.avtaler.avtaler.detaljer.adresse"
              value={`${avtale.avtaleDetaljerPrivat?.adresse}, ${formatPostnummer(avtale.avtaleDetaljerPrivat?.postnummer)} ${avtale.avtaleDetaljerPrivat?.sted}`}
            />
          ) : (
            <>
              <DescriptionTerm
                tranlsationFeltKey="pages.avtaler.avtaler.detaljer.organisasjonType"
                value={t("enums.boligType", {
                  context: avtale.avtaleDetaljerSelskap?.boligType,
                })}
              />
              <DescriptionTerm
                tranlsationFeltKey="pages.avtaler.avtaler.detaljer.organisasjonsNummer"
                value={formatOrgNr(avtale.avtaleEier.organisasjonsnummer)}
              />
            </>
          )}
          <DescriptionTerm
            tranlsationFeltKey="pages.avtaler.avtaler.detaljer.kundeNummer"
            value={avtale.kundenummer}
          />
          <DescriptionTerm
            tranlsationFeltKey="pages.avtaler.avtaler.detaljer.målerId"
            value={avtale.målerId}
          />
        </DescriptionList>
        {avtale.avtaleStatus === "Ugyldig" && (
          <UgyldigAvtaleSection avtale={avtale} />
        )}
      </Card>
      {avtale.avtaleStatus === "Aktiv" &&
        !dateHasPassed(avtale.avbestillingsFrist) && (
          <span className="text-sm text-balder-text-subtle self-end">
            {t("pages.avtaler.avtaler.detaljer.avbestilling")}{" "}
            {formatDateString(avtale.avbestillingsFrist)}
          </span>
        )}
    </div>
  );
};
